import { AppData, Lightning, Log, Utils, Router } from "@lightningjs/sdk";
import { AppSetup } from "../../lib/app-setup";
import { Helper } from "../../lib/helper";
import { fetchData, postLikeDislikeUrl } from "../../lib/models/data-service";
import { Logger } from "../../lib/logger";

export interface RateContentWidgetTemplateSpec extends Lightning.Component.TemplateSpecLoose {
  itemId: any,
}

export default class RateContentWidget
  extends Lightning.Component<RateContentWidgetTemplateSpec> {

    itemId = "";
    selectedIndex = 0;
    likeVal = 0;

    options = [
        {
            id: 1,
            label: "Like it!",
            src: Utils.asset("images/thumb-up.png"),
            focusedSrc: Utils.asset("images/hover-thumb-up.png"),
        },
        {
            id: 2,
            label: "Love it!",
            src: Utils.asset("images/love.png"),
            focusedSrc: Utils.asset("images/hover-love.png"),
        },
        {
            id: 3,
            label: "Not For Me!",
            src: Utils.asset("images/thumb-down.png"),
            focusedSrc: Utils.asset("images/hover-thumb-down.png"),
        },
    ];


    static override _template() {
        return {
            RateContentPanel: {
                w: 600,
                h: 180,
                rect: true,
                flex: { direction: "column", alignItems: "center", justifyContent: "center" },
                zIndex: 200,
                collision: true,

                ThumbButton: {
                    w: (w) => w,
                    collision: true,
                    flex: { direction: "row", alignItems: "center", justifyContent: "space-around" },
                    children: []
                },
            },
        };
    }


    override _enable() {
        const { appState } = AppData as AppSetup;
        const appApiConfig = appState?.getAppApiConfig()!;
        const bgcolor = Helper.getHexaColor(appApiConfig.appLayoutConfig.bgcolor);

        this.tag("RateContentPanel").patch({
            color: bgcolor,
            alpha: 0.9,
        });

        this._updateFocus();
    }

    override _init() {
        const types = ["Like", "Love", "Dislike"];

        const thumbChildren = types.map((name, index) => ({
            ref: `Thumb${index}`,
            w: (w) => w,
            collision: true,
            zIndex: 201,
            flex: { direction: "column", alignItems: "center", justifyContent: "center" },

            Icon: { w: 85, h: 85, src: "" },
            Label: {
                y: 10,
                h: 30,
                text: { text: name, fontSize: 18, textColor: 0xffffffff },
                alpha: 0,
            }
        }));

        this.tag("ThumbButton").patch({
            children: thumbChildren
        });
    }

    setVideoItem(nodeId: any, likeVal: any) {
        this.itemId = nodeId;
        this.likeVal = Number(likeVal);

        const initialIndex = this.options.findIndex(opt => opt.id === this.likeVal);
        this.selectedIndex = initialIndex !== -1 ? initialIndex : 0;

        this._updateFocus();
    }


    _updateFocus() {
        const { appState } = AppData as AppSetup
        const appApiConfig = appState?.getAppApiConfig();
        const likeImg = appApiConfig?.appConfig.likeImg;
        const selectedLikeImg = appApiConfig?.appConfig.likeSelectImg;
        const dislikeImg = appApiConfig?.appConfig.dislikeImg;
        const dislikeSelectImg = appApiConfig?.appConfig.dislikeSelectImg;
        const loveitImg = appApiConfig?.appConfig.loveitImg
        const loveitSelectImg = appApiConfig?.appConfig.loveitSelectImg

        const btnRow = this.tag("ThumbButton");
        this.options.forEach((opt, index) => {
            const isFocused = index === this.selectedIndex;

            const container = btnRow.children[index];
            if (!container) return; // Guard against race condition if called before init finishes
            const icon = container.tag("Icon");
            const label = container.tag("Label");

            let iconPath = isFocused ? opt.focusedSrc : opt.src;
            if (index === 0) {
                if (isFocused && selectedLikeImg) iconPath = selectedLikeImg;
                else if (!isFocused && likeImg) iconPath = likeImg;
            } else if (index === 1) {
                if (isFocused && loveitSelectImg) iconPath = loveitSelectImg;
                else if (!isFocused && loveitImg) iconPath = loveitImg;
            } else if (index === 2) {
                if (isFocused && dislikeSelectImg) iconPath = dislikeSelectImg;
                else if (!isFocused && dislikeImg) iconPath = dislikeImg;
            }

            icon.patch({
                texture: {
                    src: (typeof iconPath === 'string' && iconPath.startsWith('http')) ? iconPath : Utils.asset(iconPath),
                    resizeMode: {
                        h: 85,
                        w: 85,
                        type: 'contain',
                    },
                    type: Lightning.textures.ImageTexture
                },
            });

            label.patch({
                text: { text: opt.label },
                alpha: isFocused ? 1 : 0,
            });
        });
    }

    override _handleLeft() {
        if (this.selectedIndex > 0) {
            this.selectedIndex--;
            this._updateFocus();
        }
        return true;
    }

    override _handleRight() {
        if (this.selectedIndex < this.options.length - 1) {
            this.selectedIndex++;
            this._updateFocus();
        }
        return true;
    }

    _getThumbIndex(target: any): number {
        if (!target || typeof target.__ref !== 'string') return -1;
        const match = target.__ref.match(/^Thumb(\d)$/);
        return match ? parseInt(match[1], 10) : -1;
    }

    _handleHover(target: any) {
        const index = this._getThumbIndex(target);
        if (index !== -1) {
            this.selectedIndex = index;
            this._updateFocus();
        }
    }

    _handleClick(target: any) {
        Log.info('RateContentWidget:', '_handleClick', target);
        const index = this._getThumbIndex(target);
        if (index !== -1) {
            this.selectedIndex = index;
            this._updateFocus();
            this._handleEnter();
        }
    }

    override _handleEnter() {
        const selectValue = this.options[this.selectedIndex].id;
        const newItemVal = (this.likeVal === selectValue) ? 0 : selectValue;
        let itemType = "node" || "list";
        const url = postLikeDislikeUrl(this.itemId, itemType, newItemVal);
        let actionLabel = "Remove";
        let notificationMessage = "Rating removed!";

        if (newItemVal === 1) {
            actionLabel = "Like";
            notificationMessage = "Liked!";
        } else if (newItemVal === 2) {
            actionLabel = "Love";
            notificationMessage = "Loved it!";
        } else if (newItemVal === 3) {
            actionLabel = "Dislike";
            notificationMessage = "Not for me!";
        }

        Logger.processUserAction('Like_Dislike_Feature_' + actionLabel, this.itemId);
        this.likeVal = newItemVal;
        fetchData(url)
            .then((response) => {
                if (response.data.status === 1) {
                    Log.info("RateContentWidget: Like/DislikeElemStatus: itemId, itemType, newItemVal", this.itemId, itemType, newItemVal);
                    Router.getActivePage()?.widgets.notification.showNotification(notificationMessage);
                } else {
                    Log.error("RateContentWidget: API error:", response.data.message);                  }
            })
            .catch((err) => {
                Log.error("RateContentWidget: API call failed:", err.message);
            });

        this.signal('close', this.likeVal);
        return true;
    }

}
